// src/pages/admin/BankPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { FiDownload, FiSearch, FiRefreshCw, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { utils, writeFile } from 'xlsx';
import { montantTtc } from './lcl/utils/montantTtc';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Parse robuste des timestamps Supabase (gère "YYYY-MM-DD HH:mm:ss+00" sans T)
const parseSupabaseTs = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const s = String(value);
  const iso = s.includes('T') ? s : s.replace(' ', 'T');
  const d = new Date(iso);
  return isNaN(d) ? null : d;
};

// Date de prélèvement : date de référence + 60 jours → fin de mois
const calcDebitDate = (dateValue) => {
  const d0 = parseSupabaseTs(dateValue);
  if (!d0) return null;
  const d = new Date(d0);
  d.setDate(d.getDate() + 60);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12, 0, 0);
};

// Parse sécurisé numeric Supabase → float positif
const toPositiveFloat = (v) => {
  const n = parseFloat(String(v ?? '0').replace(/\s/g, '').replace(',', '.'));
  if (isNaN(n)) return 0;
  return n < 0 ? -n : n;
};

// Formatage euros FR
const fmt = (n) =>
  Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

// Formatage date FR
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

// ─── BankPage ─────────────────────────────────────────────────────────────────
const BankPage = () => {
  useOutletContext();

  const [allRows,  setAllRows]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [period,   setPeriod]   = useState('annual');
  const [specificMonth, setSpecificMonth] = useState(''); // 'YYYY-MM' — surcharge `period` quand renseigné
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Chargement (invoice-first : scalable avec 100k+ comptes) ───────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // A) Fetch invoices ENVOYEE directement (pas de scan 100k accounts)
      const { data: invoices, error: invErr } = await supabase
        .from('invoices')
        .select('id, account_id, statut, sent_at, montant_facture, exercice_annee, numero_facture, url_pdf, date_prelevement')
        .eq('statut', 'ENVOYEE')
        .not('sent_at', 'is', null)
        .order('sent_at', { ascending: false })
        .limit(2000);

      if (invErr) {
        console.error('[BankPage] Erreur fetch invoices:', invErr);
        throw invErr;
      }

      if (!invoices?.length) { setAllRows([]); return; }

      // B) Liste unique des account_id
      const accountIds = [...new Set(invoices.map((inv) => inv.account_id).filter(Boolean))];

      // C) Fetch uniquement les comptes concernés
      let accountsById = {};
      if (accountIds.length > 0) {
        const { data: accounts, error: accErr } = await supabase
          .from('accounts')
          .select('id, company_name, contact_full_name, address, postal_code, city, iban, bic, sepa_mandate_rum')
          .in('id', accountIds);

        if (accErr) {
          // RLS ou autre erreur : on continue avec des placeholders
          console.error('[BankPage] Erreur fetch accounts (RLS?):', accErr);
        } else {
          for (const acc of (accounts || [])) {
            accountsById[acc.id] = acc;
          }
        }
      }

      // D) Construire les rows à partir des invoices
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const rows = [];

      for (const inv of invoices) {
        const sentAt = parseSupabaseTs(inv.sent_at);
        if (!sentAt) {
          console.warn('[BankPage] sent_at non parsable:', inv.sent_at, '| invoice:', inv.id);
          continue;
        }

        // date_prelevement : priorité DB, fallback calcul
        const datePrelevement = inv.date_prelevement
          ? parseSupabaseTs(inv.date_prelevement)
          : calcDebitDate(inv.sent_at);
        if (!datePrelevement) {
          console.warn('[BankPage] datePrelevement null pour invoice:', inv.id);
          continue;
        }

        // invoices.montant_facture est HT en BDD. On expose les 2 colonnes :
        // - montant     = HT (référence comptable)
        // - montantTtc  = TTC (= prélèvement réel via SEPA, cohérent avec
        //                 la facture envoyée au client)
        const montant = toPositiveFloat(inv.montant_facture);
        if (montant <= 0) continue;
        const montantTtcVal = montantTtc(montant);

        const client = accountsById[inv.account_id] || null;
        const statut = datePrelevement < today ? 'Exécuté' : 'À venir';

        rows.push({
          rowKey:          `${inv.account_id}-${inv.id}`,
          clientId:        inv.account_id,
          clientName:      client
                             ? (client.company_name || client.contact_full_name || '—')
                             : 'Compte non accessible',
          adresse:         client
                             ? [client.address, client.postal_code, client.city].filter(Boolean).join(' ')
                             : '—',
          iban:            client?.iban || '—',
          bic:             client?.bic  || '—',
          numeroMandat:    client?.sepa_mandate_rum || '—',
          invoiceId:       inv.id,
          numeroFacture:   inv.numero_facture || '—',
          exerciceAnnee:   inv.exercice_annee || '—',
          url_pdf:         inv.url_pdf,
          dateEnvoi:       sentAt,
          datePrelevement,
          montant,
          montantTtc:      montantTtcVal,
          invoiceStatut:   inv.statut,
          statut,
        });
      }

      setAllRows(rows);
    } catch (err) {
      console.error('[BankPage] Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtrage ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let arr = [...allRows];
    const now = new Date();

    if (specificMonth) {
      const [y, m] = specificMonth.split('-').map(Number);
      if (y && m) {
        const start = new Date(y, m - 1, 1);
        const end   = new Date(y, m, 0, 23, 59, 59);
        arr = arr.filter(
          (r) => r.datePrelevement && r.datePrelevement >= start && r.datePrelevement <= end
        );
      }
    } else if (period !== 'all') {
      let start, end;
      if (period === 'weekly') {
        const day = now.getDay() || 7;
        start = new Date(now); start.setDate(now.getDate() - day + 1); start.setHours(0, 0, 0, 0);
        end   = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
      } else if (period === 'monthly') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (period === 'annual') {
        start = new Date(now.getFullYear(), 0, 1);
        end   = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      }
      if (start && end) {
        arr = arr.filter(
          (r) => r.datePrelevement && r.datePrelevement >= start && r.datePrelevement <= end
        );
      }
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((r) =>
        [r.clientName, r.numeroMandat, r.adresse, r.iban, r.statut, r.numeroFacture, String(r.exerciceAnnee)]
          .some((v) => String(v ?? '').toLowerCase().includes(q))
      );
    }

    return arr;
  }, [allRows, period, specificMonth, search]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total: filtered.reduce((s, r) => s + r.montant, 0),
    count: filtered.length,
    moyen: filtered.length
      ? filtered.reduce((s, r) => s + r.montant, 0) / filtered.length
      : 0,
  }), [filtered]);

  // ── Modification date de prélèvement ────────────────────────────────────
  const handleSaveDate = useCallback(async (invoiceId, rowKey) => {
    if (!editDateValue) return;
    setSaving(true);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) { alert('Session expirée.'); return; }

      const res = await fetch(`/api/invoices/${invoiceId}/date-prelevement`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({ date_prelevement: editDateValue }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur serveur');

      // Mettre à jour la row localement
      const newDate = parseSupabaseTs(editDateValue);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      setAllRows((prev) =>
        prev.map((r) =>
          r.rowKey === rowKey
            ? { ...r, datePrelevement: newDate, statut: newDate < today ? 'Exécuté' : 'À venir' }
            : r
        )
      );
      setEditingRowKey(null);
    } catch (err) {
      console.error('[BankPage] Erreur update date_prelevement:', err);
      alert(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [editDateValue]);

  // ── Export Excel ─────────────────────────────────────────────────────────
  // Export = HT + TTC en 2 colonnes côte à côte. HT = référence comptable,
  // TTC = montant prélevé via SEPA / encaissé.
  const handleExport = () => {
    const ws = utils.json_to_sheet(
      filtered.map((r) => ({
        'Client':             r.clientName,
        'N° Facture':         r.numeroFacture,
        'Exercice':           r.exerciceAnnee,
        'Commission HT (€)':  r.montant.toFixed(2),
        'Commission TTC (€)': r.montantTtc.toFixed(2),
        'Date envoi':         fmtDate(r.dateEnvoi),
        'Date prélèvement':   fmtDate(r.datePrelevement),
        'N° Mandat SEPA':     r.numeroMandat,
        'IBAN':               r.iban,
        'BIC':                r.bic,
        'Adresse':            r.adresse,
        'Statut':             r.statut,
      }))
    );
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Prélèvements');
    writeFile(wb, `Prelevements_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Rendu ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-3">
      <div className="max-w-7xl mx-auto space-y-3">

        {/* Header */}
        <div className="bg-white rounded shadow border border-gray-200 px-3 py-2
                        flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-gray-900">Gestion des Prélèvements</div>
            <div className="text-[10px] text-gray-500">
              Ordres de prélèvement SEPA — basés sur les factures envoyées
            </div>
          </div>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px]
                       font-semibold text-white bg-green-600 hover:bg-green-700"
          >
            <FiDownload className="h-3.5 w-3.5" />
            Exporter
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Total commissions',  value: fmt(stats.total), color: 'text-green-700'  },
            { label: 'Nb prélèvements',    value: stats.count,      color: 'text-blue-700'   },
            { label: 'Montant moyen',       value: fmt(stats.moyen), color: 'text-purple-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[10px] text-gray-500 font-medium">{label}</div>
              <div className={`text-base font-bold ${color}`}>{value}</div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div className="bg-white rounded border border-gray-200 p-2
                        flex flex-wrap items-center gap-2">

          <div className="relative flex-1 min-w-[140px]">
            <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, IBAN, mandat, n° facture…"
              className="w-full pl-7 pr-2 py-1 text-[11px] border border-gray-200 rounded
                         focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="inline-flex rounded border border-gray-200 overflow-hidden">
            {[
              { key: 'monthly', label: 'Ce mois' },
              { key: 'weekly',  label: 'Semaine' },
              { key: 'annual',  label: 'Année'   },
              { key: 'all',     label: 'Tout'    },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setPeriod(key); setSpecificMonth(''); }}
                className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  !specificMonth && period === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <input
            type="month"
            value={specificMonth}
            onChange={(e) => setSpecificMonth(e.target.value)}
            title="Filtrer par mois précis"
            className={`px-2 py-1 text-[11px] border rounded focus:ring-1 focus:ring-blue-500 ${
              specificMonth ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200'
            }`}
          />
          {specificMonth && (
            <button
              onClick={() => setSpecificMonth('')}
              className="text-[11px] px-1.5 py-1 text-gray-400 hover:text-gray-700"
              title="Effacer le filtre mois"
            >
              <FiX className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded
                       border border-gray-200 hover:bg-gray-50 text-gray-600"
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-xs text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
              Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-400">
              Aucun prélèvement pour cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'Client', 'Facture', 'Commission HT', 'Commission TTC',
                      'Envoyée le', 'Prélèvement', 'N° Mandat',
                      'IBAN', 'Statut',
                    ].map((h) => (
                      <th key={h}
                        className="px-3 py-2 text-left text-[10px] font-semibold
                                   text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((row) => (
                    <tr key={row.rowKey} className="hover:bg-gray-50">

                      {/* Client */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-[11px] font-semibold text-gray-900">
                          {row.clientName}
                        </div>
                      </td>

                      {/* Facture */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-[11px] font-medium text-gray-800">
                          {row.numeroFacture}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          Exercice {row.exerciceAnnee}
                        </div>
                      </td>

                      {/* Commission HT */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] font-semibold text-gray-700">
                        {fmt(row.montant)}
                      </td>

                      {/* Commission TTC (prélevé) */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] font-bold text-green-700" title="Montant prélevé via SEPA (HT × 1.20)">
                        {fmt(row.montantTtc)}
                      </td>

                      {/* Date envoi */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">
                        {fmtDate(row.dateEnvoi)}
                      </td>

                      {/* Date prélèvement (éditable) */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] font-semibold text-blue-700">
                        {editingRowKey === row.rowKey ? (
                          <span className="inline-flex items-center gap-1">
                            <input
                              type="date"
                              value={editDateValue}
                              onChange={(e) => setEditDateValue(e.target.value)}
                              className="border border-blue-300 rounded px-1 py-0.5 text-[11px] w-[120px]
                                         focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={() => handleSaveDate(row.invoiceId, row.rowKey)}
                              disabled={saving}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                              title="Enregistrer"
                            >
                              <FiCheck className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => setEditingRowKey(null)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Annuler"
                            >
                              <FiX className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 group">
                            {fmtDate(row.datePrelevement)}
                            <button
                              onClick={() => {
                                setEditingRowKey(row.rowKey);
                                setEditDateValue(
                                  row.datePrelevement
                                    ? new Date(row.datePrelevement).toISOString().slice(0, 10)
                                    : ''
                                );
                              }}
                              className="opacity-0 group-hover:opacity-100 text-gray-400
                                         hover:text-blue-600 transition-opacity"
                              title="Modifier la date de prélèvement"
                            >
                              <FiEdit2 className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </td>

                      {/* Mandat */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500 font-mono">
                        {row.numeroMandat}
                      </td>

                      {/* IBAN */}
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500 font-mono">
                        {row.iban}
                      </td>

                      {/* Statut */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded
                                         text-[10px] font-semibold ${
                          row.statut === 'Exécuté'  ? 'bg-green-100 text-green-800' :
                          row.statut === 'À venir'  ? 'bg-blue-100  text-blue-800'  :
                                                      'bg-gray-100  text-gray-600'
                        }`}>
                          {row.statut}
                        </span>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default BankPage;
